import React, { useCallback, useEffect, useRef, useState } from 'react';
import { PageTitle, HeavyText, LightText, Image, LightItalicText } from '../components/Style';

import styled, { keyframes } from 'styled-components';
import { Button, Card, Form, Input, FormError } from '../components/FormStyle';
import Search from '../components/Search';
import PromptResponseBanner from '../components/PromptResponseBanner';
import { useAuth } from '../context/auth';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { MediaHeader } from '../components/MediaHeader';
import { MediaArt } from '../components/MediaArt';
import Media from '../types/Media';
import MentionInput from '../components/MentionInput';
import api from '../api';
import { resolveMusicMentions, enrichOrderedMentions } from '../mentionUtils';
import type { MusicMentionData } from '../mentionUtils';
import { usePlayback } from '../context/playback';
import RecentlyPlayed from '../components/RecentlyPlayed';
import { trackEvent } from '../analytics';
import { ReactComponent as PlayIcon } from '../img/play.svg';
import { ReactComponent as PauseIcon } from '../img/pause.svg';
// @ts-ignore
var CreatableSelect = require('react-select/creatable').default;

const ClearMediaButton = styled.button`
  background: none;
  border: none;
  color: #999;
  font-size: 0.8rem;
  cursor: pointer;
  padding: 0;
  font-family: inherit;
  text-decoration: underline;
  align-self: flex-start;

  &:hover {
    color: #333;
  }
`;

const ArtworkBlock = styled.div`
  border: 1px solid black;
  border-right-width: 2px;
  border-bottom-width: 2px;
  border-radius: 8px;
  overflow: hidden;
  margin: 1rem 0 0.5rem 0;
`;

const PageHeader = styled.h1`
  font-weight: 900;
  font-size: 1.4rem;
  margin: 0.5rem 0 0.25rem 0;
`;

const SubText = styled.p`
  color: #888;
  font-size: 0.9rem;
  font-weight: 300;
  margin: 0 0 1rem 0;
`;

const AddNoteToggle = styled.button`
  background: none;
  border: none;
  color: #888;
  font-size: 0.9rem;
  cursor: pointer;
  padding: 0.5rem 0;
  text-decoration: underline;
`;

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const scaleIn = keyframes`
  0%   { transform: scale(0); }
  60%  { transform: scale(1.15); }
  100% { transform: scale(1); }
`;

const SuccessWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 2rem 0;
  animation: ${fadeIn} 0.4s ease-out;
`;

const Checkmark = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: #2ecc71;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: ${scaleIn} 0.5s ease-out;
  margin-bottom: 1rem;

  &::after {
    content: '';
    display: block;
    width: 20px;
    height: 32px;
    border: solid white;
    border-width: 0 4px 4px 0;
    transform: rotate(45deg);
    margin-top: -4px;
  }
`;

const SuccessText = styled.p`
  font-weight: 700;
  font-size: 1.1rem;
  margin: 0 0 1.5rem 0;
`;

const FeedLink = styled(Link)`
  background: white;
  border: 1px solid black;
  border-right-width: 2px;
  border-bottom-width: 2px;
  border-radius: 8px;
  padding: 0.5rem 1rem;
  color: black;
  font-weight: 700;
  font-size: 0.8rem;
  text-decoration: none;
  cursor: pointer;
  animation: ${fadeIn} 0.4s ease-out 0.3s both;
`;

const PlayButton = styled.button`
  background: none;
  border: none;
  padding: 0.25rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  color: white;
  font-size: 1.2rem;

  &:hover {
    opacity: 0.7;
  }

  &:disabled {
    opacity: 0.4;
    cursor: default;
  }
`;

const QueueMenuOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1100;
`;

const QueueMenuPopup = styled.div`
  position: fixed;
  background: white;
  color: black;
  border: 1px solid black;
  border-right-width: 2px;
  border-bottom-width: 2px;
  border-radius: 8px;
  padding: 4px 0;
  z-index: 1101;
  min-width: 180px;
`;

const QueueMenuItem = styled.button`
  display: block;
  width: 100%;
  background: none;
  border: none;
  padding: 8px 12px;
  font-size: 0.8rem;
  color: black;
  text-align: left;
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    background: #f0f0f0;
  }
`;

const PromptSection = styled.div`
  margin: 0 0 0.5rem 0;
`;

const PromptLabel = styled.div`
  font-size: 0.8rem;
  color: #888;
  font-weight: 600;
  margin-bottom: 0.4rem;
`;

const PromptPill = styled.button<{ $selected: boolean }>`
  display: inline-flex;
  align-items: center;
  background: white;
  color: #333;
  border: 1px solid black;
  border-right-width: 2px;
  border-bottom-width: 2px;
  border-radius: 999px;
  padding: 0 0.65em;
  height: 26px;
  box-sizing: content-box;
  font-size: 0.72rem;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;

  &:hover {
    opacity: 0.85;
  }
`;

const ModeTabs = styled.div`
  display: flex;
  justify-content: center;
  gap: 1.25rem;
  margin-bottom: 1rem;
`;

const ModeTab = styled.button<{ $active: boolean }>`
  background: none;
  border: none;
  border-bottom: 2px solid ${(p) => (p.$active ? '#000' : 'transparent')};
  color: ${(p) => (p.$active ? '#000' : '#999')};
  font-weight: ${(p) => (p.$active ? 700 : 400)};
  font-size: 0.85rem;
  font-family: inherit;
  padding: 0.25rem 0.25rem 0.4rem;
  cursor: pointer;
`;

export enum PostStatus {
  NONE = 'none',
  INPROGRESS = 'in_progress',
  COMPLETE = 'completed',
  ERROR = 'error',
}

export default function CreatePost() {
  const location = useLocation();

  useEffect(function () {
    document.title = 'New Post | HeavyRotation';
  }, []);

  const [mediaItem, setMediaItem] = useState<Media | undefined>(undefined);
  const artworkOverrideRef = useRef<string | null>(null);
  const prePopulated = useRef(false);
  const [postText, setPostText] = useState('');
  const [noteExpanded, setNoteExpanded] = useState(false);
  const { authTokens } = useAuth();
  const [postState, setPostState] = useState(PostStatus.NONE);
  const [errorText, setErrorText] = useState('');
  const [musicMentions, setMusicMentions] = useState<MusicMentionData[]>([]);
  const getSubmitTextRef = useRef<(() => { text: string; mentions: any[] }) | null>(null);
  const [activePrompts, setActivePrompts] = useState<
    Array<{ id: string; name: string; text: string }>
  >([]);
  const [selectedPromptId, setSelectedPromptId] = useState<string | null>(null);
  const [searchParams] = useSearchParams();
  const arrivedWithPromptId = useRef(!!searchParams.get('promptId'));
  const playback = usePlayback();
  const musicKit = {
    isAuthorized: playback.isAppleMusicConnected,
    isLoading: playback.musicKitLoading,
    authorize: playback.authorize,
  };
  const [fetchingPreview, setFetchingPreview] = useState(false);
  const cachedPreviewRef = useRef<{ previewURL: string | null; catalogID: string | null } | null>(
    null
  );
  const [menuPos, setMenuPos] = useState<{ x: number; y: number } | null>(null);
  const [mode, setMode] = useState<'song' | 'ask'>(
    searchParams.get('kind') === 'ask' ? 'ask' : 'song'
  );
  const [requestText, setRequestText] = useState('');
  const [requestMentions, setRequestMentions] = useState<MusicMentionData[]>([]);
  const requestSubmitRef = useRef<(() => { text: string; mentions: any[] }) | null>(null);

  // Fetch active prompts and handle ?promptId= URL param
  useEffect(function () {
    api
      .get('/api/prompts/active')
      .then(function (response: any) {
        var prompts = (response.data || []).map(function (p: any) {
          return { id: p.id, name: p.name, text: p.text };
        });
        // Merge instead of replacing: the ?promptId= lookup below runs in
        // parallel and may have already added a prompt that is outside the
        // active window. Replacing here would drop it and lose the banner.
        setActivePrompts(function (prev) {
          var known = prompts.map(function (p: any) {
            return p.id;
          });
          var extras = prev.filter(function (p) {
            return known.indexOf(p.id) === -1;
          });
          return extras.concat(prompts);
        });
      })
      .catch(function () {});

    var urlPromptId = searchParams.get('promptId');
    if (urlPromptId) {
      setSelectedPromptId(urlPromptId);
      // If the prompt isn't in the active list, fetch it individually
      api
        .get('/api/prompts/' + urlPromptId)
        .then(function (response: any) {
          var p = response.data;
          setActivePrompts(function (prev: any) {
            if (
              prev.some(function (x: any) {
                return x.id === p.id;
              })
            )
              return prev;
            return [{ id: p.id, name: p.name, text: p.text }].concat(prev);
          });
        })
        .catch(function () {});
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-populate from navigation state (e.g. "Post about this album")
  useEffect(function () {
    if (prePopulated.current) return;
    var state = location.state as any;
    if (state && state.media) {
      prePopulated.current = true;
      var m = state.media;
      // Preserve the artwork from navigation state (e.g. specific album art)
      // so we can send it as an override if it differs from the media record
      if (m.artwork_url) {
        artworkOverrideRef.current = m.artwork_url;
      }
      // If the media already has a DB id, use it directly
      if (m.id) {
        setMediaItem(m);
      } else if (m.isrc || m.upc) {
        // Has identifiers - create/find via API
        updateMediaItem(m);
      } else {
        // No ISRC or UPC, so /api/media has nothing to key a record on and will
        // reject it. Show the item, but say up front that it cannot be posted
        // rather than letting the user write a note against a dead button.
        setMediaItem(m);
        setPostState(PostStatus.ERROR);
        setErrorText("Can't post this one. Try searching for it instead.");
      }
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset cached preview when media changes
  useEffect(
    function () {
      cachedPreviewRef.current = null;
    },
    [mediaItem?.id]
  );

  var canPlay = mediaItem
    ? !!mediaItem.isrc ||
      !!mediaItem.upc ||
      !!(mediaItem as any).previewURL ||
      !!(mediaItem as any).catalogID
    : false;
  var previewPostId = mediaItem?.id || 'create-preview';
  var isThisPlaying = playback.currentTrack && playback.currentTrack.postId === previewPostId;
  var isSomethingPlaying =
    playback.playbackState === 'playing' ||
    playback.playbackState === 'paused' ||
    playback.playbackState === 'loading';
  var playState: 'idle' | 'loading' | 'playing' | 'paused' = 'idle';
  if (isThisPlaying) {
    playState = playback.playbackState as any;
  }

  function buildQueueItems(trackData: any): any[] {
    if (!mediaItem) return [];
    if (trackData.tracks) {
      return trackData.tracks.map(function (t: any) {
        return {
          postId: previewPostId,
          title: t.title,
          artist: mediaItem.artist,
          artworkURL: mediaItem.artwork_url,
          catalogID: t.catalogID || null,
          previewURL: t.previewURL || null,
        };
      });
    }
    return [
      {
        postId: previewPostId,
        title: mediaItem.song_title || mediaItem.album_title,
        artist: mediaItem.artist,
        artworkURL: mediaItem.artwork_url,
        catalogID: trackData.catalogID || null,
        previewURL: trackData.previewURL || null,
      },
    ];
  }

  function fetchAndPlay(mode: 'playNow' | 'addToQueue') {
    if (!mediaItem) return;
    setFetchingPreview(true);
    setMenuPos(null);

    if (mediaItem.type === 'album' && mediaItem.upc) {
      api
        .get('/api/music/album-tracks', { params: { upc: mediaItem.upc } })
        .then(function (response) {
          setFetchingPreview(false);
          var items = buildQueueItems({ tracks: response.data.tracks || [] });
          if (items.length === 0) return;
          if (mode === 'playNow') {
            playback.playNow(items);
          } else {
            playback.addToQueue(items);
          }
        })
        .catch(function () {
          setFetchingPreview(false);
        });
    } else {
      if (cachedPreviewRef.current) {
        setFetchingPreview(false);
        var items = buildQueueItems(cachedPreviewRef.current);
        if (mode === 'playNow') {
          playback.playNow(items);
        } else {
          playback.addToQueue(items);
        }
        return;
      }

      var params = mediaItem.isrc ? { isrc: mediaItem.isrc } : { upc: mediaItem.upc };
      api
        .get('/api/music/preview', { params: params })
        .then(function (response) {
          var previewURL = response.data.previewURL || null;
          var catalogID = response.data.catalogID || null;
          cachedPreviewRef.current = { previewURL: previewURL, catalogID: catalogID };
          setFetchingPreview(false);
          var items = buildQueueItems({ previewURL: previewURL, catalogID: catalogID });
          if (mode === 'playNow') {
            playback.playNow(items);
          } else {
            playback.addToQueue(items);
          }
        })
        .catch(function () {
          setFetchingPreview(false);
        });
    }
  }

  function handlePlay(e: React.MouseEvent) {
    if (isThisPlaying) {
      playback.togglePlayPause();
      return;
    }
    if (!isSomethingPlaying) {
      fetchAndPlay('playNow');
      return;
    }
    var rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    setMenuPos({ x: rect.left, y: rect.top });
  }

  const handlePost = useCallback(() => {
    if (mediaItem == null || !mediaItem.id) {
      setPostState(PostStatus.ERROR);
      setErrorText("Can't post this one. Try searching for it instead.");
      return;
    }

    const submitData = getSubmitTextRef.current ? getSubmitTextRef.current() : null;
    const finalText = (submitData ? submitData.text : null) || postText;
    const orderedMentions = submitData
      ? enrichOrderedMentions(submitData.mentions, musicMentions)
      : musicMentions;

    resolveMusicMentions(orderedMentions).then(function (resolved) {
      // Extract hashtags from text
      var extractedTags: string[] = [];
      if (finalText) {
        var tagMatches = finalText.match(/#([a-zA-Z0-9_]+)/g);
        if (tagMatches) {
          tagMatches.forEach(function (t: string) {
            var name = t.slice(1).toLowerCase();
            if (extractedTags.indexOf(name) === -1) extractedTags.push(name);
          });
        }
      }
      const request: any = {
        user: { id: authTokens.user.id },
        media: { id: mediaItem!.id },
        text: finalText,
        tags: extractedTags,
      };
      if (resolved.length > 0) {
        request.musicMentions = resolved;
      }
      if (selectedPromptId) {
        request.promptId = selectedPromptId;
      }
      // Send artwork override if the user came from a specific album context
      // and the artwork differs from the media record
      if (
        artworkOverrideRef.current &&
        mediaItem?.artwork_url &&
        artworkOverrideRef.current !== mediaItem.artwork_url
      ) {
        request.artworkURLOverride = artworkOverrideRef.current;
      }
      api
        .post('/api/posts', request)
        .then(function () {
          setPostState(PostStatus.COMPLETE);
          trackEvent('post_created', { source: prePopulated.current ? 'music_page' : 'direct' });
        })
        .catch(function (error: any) {
          setPostState(PostStatus.ERROR);
          setErrorText((error.response && error.response.statusText) || 'An error occurred');
        });
    });
  }, [mediaItem, postText, authTokens, musicMentions, selectedPromptId]);

  const handleAskSubmit = useCallback(() => {
    const submitData = requestSubmitRef.current ? requestSubmitRef.current() : null;
    const finalText = ((submitData ? submitData.text : null) || requestText).trim();
    if (!finalText) {
      setPostState(PostStatus.ERROR);
      setErrorText('Add a question first');
      return;
    }
    const orderedMentions = submitData
      ? enrichOrderedMentions(submitData.mentions, requestMentions)
      : requestMentions;
    resolveMusicMentions(orderedMentions).then(function (resolved) {
      var extractedTags: string[] = [];
      var tagMatches = finalText.match(/#([a-zA-Z0-9_]+)/g);
      if (tagMatches) {
        tagMatches.forEach(function (t: string) {
          var name = t.slice(1).toLowerCase();
          if (extractedTags.indexOf(name) === -1) extractedTags.push(name);
        });
      }
      const request: any = {
        user: { id: authTokens.user.id },
        kind: 'ask',
        text: finalText,
        tags: extractedTags,
      };
      if (resolved.length > 0) {
        request.musicMentions = resolved;
      }
      api
        .post('/api/posts', request)
        .then(function () {
          setPostState(PostStatus.COMPLETE);
          trackEvent('post_created', { source: 'ask' });
        })
        .catch(function (error: any) {
          setPostState(PostStatus.ERROR);
          setErrorText((error.response && error.response.statusText) || 'An error occurred');
        });
    });
  }, [requestText, requestMentions, authTokens]);

  function clearPostError() {
    setPostState(PostStatus.NONE);
    setErrorText('');
  }

  function updateMediaItem(incomingMediaItem: Media | null) {
    clearPostError();

    if (!incomingMediaItem) {
      setMediaItem(undefined);
      return;
    }

    if (incomingMediaItem.id != null) {
      setMediaItem(incomingMediaItem);
      return;
    }

    api
      .post('/api/media', incomingMediaItem)
      .then((response) => response.data)
      .then((data) => setMediaItem(data))
      .catch((error) => {
        setPostState(PostStatus.ERROR);
        setErrorText((error.response && error.response.statusText) || 'Failed to save media');
      });
  }

  if (postState === PostStatus.COMPLETE) {
    return (
      <Card style={{ marginTop: '2rem' }}>
        <SuccessWrap>
          <Checkmark />
          <SuccessText>Posted!</SuccessText>
          <FeedLink to="/">Return to feed</FeedLink>
        </SuccessWrap>
      </Card>
    );
  }

  var matchedPrompt = selectedPromptId
    ? activePrompts.find(function (p) {
        return p.id === selectedPromptId;
      })
    : null;

  function renderPromptBanner() {
    if (!arrivedWithPromptId.current || !matchedPrompt) return null;
    return (
      <PromptResponseBanner
        text={matchedPrompt.text}
        onRemove={function () {
          setSelectedPromptId(null);
        }}
      />
    );
  }

  if (mode === 'ask') {
    return (
      <Card style={{ marginTop: '2rem' }}>
        <Form>
          <ModeTabs>
            <ModeTab
              $active={false}
              onClick={function () {
                setMode('song');
              }}
            >
              Share Music
            </ModeTab>
            <ModeTab
              $active={true}
              onClick={function () {
                setMode('ask');
              }}
            >
              Ask the Community
            </ModeTab>
          </ModeTabs>
          <PageHeader>Ask the Community</PageHeader>
          <SubText>
            Looking for something new? Need a suggestion? Tell the community what you're after.
          </SubText>
          <MentionInput
            name="requestText"
            value={requestText}
            onChange={function (e: any) {
              setRequestText(e.target.value);
            }}
            musicMentions={requestMentions}
            onMusicMentionsChange={setRequestMentions}
            getSubmitTextRef={requestSubmitRef}
            minRows={4}
            autoFocus
            style={{ width: '100%', boxSizing: 'border-box' }}
          />
          <Button style={{ marginTop: '1rem' }} onClick={handleAskSubmit}>
            Ask
          </Button>
          {postState == PostStatus.ERROR && <FormError>{errorText}</FormError>}
        </Form>
      </Card>
    );
  }

  if (mediaItem !== undefined) {
    let media: Media = mediaItem;
    return (
      <Card style={{ marginTop: '2rem' }}>
        <Form>
          {renderPromptBanner()}
          {!(arrivedWithPromptId.current && selectedPromptId) && (
            <PageHeader>
              {prePopulated.current ? 'Create a Post' : 'What are you listening to?'}
            </PageHeader>
          )}
          {!prePopulated.current && <SubText>Search for a song, album, or artist</SubText>}
          {!prePopulated.current && (
            <>
              {musicKit.isAuthorized && (
                <RecentlyPlayed onSelect={updateMediaItem} musicKit={musicKit} />
              )}
              <Search setMediaItem={updateMediaItem} autoFocus />
              {!musicKit.isAuthorized && (
                <RecentlyPlayed onSelect={updateMediaItem} musicKit={musicKit} />
              )}
            </>
          )}
          {prePopulated.current && (
            <ClearMediaButton
              onClick={function () {
                prePopulated.current = false;
                clearPostError();
                setMediaItem(undefined);
              }}
            >
              Change song or album
            </ClearMediaButton>
          )}
          <ArtworkBlock>
            <MediaHeader
              media={media}
              playButton={
                canPlay ? (
                  <PlayButton
                    onClick={handlePlay}
                    disabled={playState === 'loading' || fetchingPreview}
                  >
                    {playState === 'loading' || fetchingPreview ? (
                      '...'
                    ) : playState === 'playing' ? (
                      <PauseIcon width="16" height="16" />
                    ) : (
                      <PlayIcon width="16" height="16" />
                    )}
                  </PlayButton>
                ) : undefined
              }
            />
            <MediaArt media={media} size="400x400" />
          </ArtworkBlock>
          {matchedPrompt && (
            <PromptSection>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
                <PromptPill $selected={true} as="span">
                  {matchedPrompt.name || matchedPrompt.text}
                </PromptPill>
                <button
                  onClick={function () {
                    setSelectedPromptId(null);
                  }}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: '1.1rem',
                    color: '#999',
                    lineHeight: 1,
                    padding: 0,
                  }}
                  aria-label="Remove prompt"
                >
                  &times;
                </button>
              </div>
            </PromptSection>
          )}
          <Button onClick={handlePost} disabled={!mediaItem || !mediaItem.id}>
            Post
          </Button>
          {!noteExpanded && (
            <AddNoteToggle
              onClick={function () {
                setNoteExpanded(true);
              }}
            >
              Add a note (optional)
            </AddNoteToggle>
          )}
          {noteExpanded && (
            <MentionInput
              name="postText"
              value={postText}
              onChange={function (e: any) {
                setPostText(e.target.value);
              }}
              musicMentions={musicMentions}
              onMusicMentionsChange={setMusicMentions}
              getSubmitTextRef={getSubmitTextRef}
              minRows={5}
              style={{ width: '100%', boxSizing: 'border-box' }}
            />
          )}
          {/* Tags hidden for now
          <CreatableSelect
            isMulti
            isOptionDisabled={function() { return selectedTags.length >= 3; }}
            placeholder="Add tags (e.g. #chill, #workout)"
            value={selectedTags}
            options={tagOptions}
            onChange={function(val: any) { setSelectedTags(val || []); }}
            onInputChange={function(val: string, meta: any) { if (meta.action === "input-change") { loadTagOptions(val); } }}
            formatCreateLabel={function(input: string) { return "Create \"#" + input + "\""; }}
            styles={{
              control: function(base: any) { return Object.assign({}, base, { fontSize: "0.9rem", marginTop: "0.5rem" }); },
              multiValue: function(base: any) { return Object.assign({}, base, { borderRadius: "4px" }); }
            }}
          />
          */}
          {postState == PostStatus.ERROR && <FormError>{errorText}</FormError>}
        </Form>
        {menuPos && (
          <>
            <QueueMenuOverlay
              onClick={function () {
                setMenuPos(null);
              }}
            />
            <QueueMenuPopup
              style={{
                left: Math.max(8, Math.min(menuPos.x, window.innerWidth - 188)),
                top: Math.max(8, Math.min(menuPos.y - 80, window.innerHeight - 88)),
              }}
            >
              <QueueMenuItem
                onClick={function () {
                  fetchAndPlay('playNow');
                }}
              >
                Play Now
              </QueueMenuItem>
              <QueueMenuItem
                onClick={function () {
                  fetchAndPlay('addToQueue');
                }}
              >
                Add to Queue
              </QueueMenuItem>
            </QueueMenuPopup>
          </>
        )}
      </Card>
    );
  }

  return (
    <Card style={{ marginTop: '2rem' }}>
      <Form>
        {!arrivedWithPromptId.current && (
          <ModeTabs>
            <ModeTab
              $active={true}
              onClick={function () {
                setMode('song');
              }}
            >
              Share Music
            </ModeTab>
            <ModeTab
              $active={false}
              onClick={function () {
                setMode('ask');
              }}
            >
              Ask the Community
            </ModeTab>
          </ModeTabs>
        )}
        {arrivedWithPromptId.current &&
          selectedPromptId &&
          (function () {
            var matchedPrompt = activePrompts.find(function (p) {
              return p.id === selectedPromptId;
            });
            return matchedPrompt ? (
              <PromptResponseBanner
                text={matchedPrompt.text}
                onRemove={function () {
                  setSelectedPromptId(null);
                }}
              />
            ) : null;
          })()}
        {!(arrivedWithPromptId.current && selectedPromptId) && (
          <PageHeader>What are you listening to?</PageHeader>
        )}
        <SubText>Search for a song, album, or artist</SubText>
        {musicKit.isAuthorized && <RecentlyPlayed onSelect={updateMediaItem} musicKit={musicKit} />}
        <Search setMediaItem={updateMediaItem} autoFocus />
        {!musicKit.isAuthorized && (
          <RecentlyPlayed onSelect={updateMediaItem} musicKit={musicKit} />
        )}
        {postState == PostStatus.ERROR && <FormError>{errorText}</FormError>}
      </Form>
    </Card>
  );
}
